import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listKafDomains, listKafProgress, type KafDomain } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { SpiralJourney, type SpiralPoint } from "@/academy/engines/living/SpiralJourney";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_authenticated/academy/kaf/journey")({
  head: () => ({ meta: [{ title: "Journey — KAfrequency" }] }),
  component: JourneyPage,
});

function JourneyPage() {
  const domainsFn = useServerFn(listKafDomains);
  const progressFn = useServerFn(listKafProgress);
  const domains = useQuery({ queryKey: ["kaf", "domains"], queryFn: () => domainsFn() });
  const progress = useQuery({ queryKey: ["kaf", "progress"], queryFn: () => progressFn() });
  const [selected, setSelected] = useState<SpiralPoint | null>(null);

  const completedIds = new Set((progress.data ?? []).filter((p) => p.status === "completed").map((p) => p.lesson_id));

  const points = useMemo<SpiralPoint[]>(
    () => ((domains.data ?? []) as KafDomain[]).map((d) => ({
      id: d.id, slug: d.slug, label: d.name, hue: d.hue, order: d.spiral_order,
      completed: completedIds.has(d.id),
    })),
    [domains.data, progress.data],
  );

  const selDomain = selected ? ((domains.data ?? []) as KafDomain[]).find((d) => d.id === selected.id) : null;

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0"><LivingBackdrop hue={44} density={100} /></div>
      <section className="relative z-10 mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Your Spiral</p>
        <h1 className="mt-2 font-serif text-4xl text-gold-luminous">The Living Journey</h1>
        <p className="mt-3 max-w-2xl text-foreground/70">Every completed lesson brightens a node. Zoom into a domain to travel deeper.</p>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr,320px]">
          <SpiralJourney points={points} onSelect={(p) => setSelected(p)} selectedId={selected?.id ?? null} height={620} />
          <aside className="rounded-2xl border hairline bg-obsidian/70 p-5">
            {selDomain ? (
              <>
                <div className="text-xs uppercase tracking-[0.3em]" style={{ color: `hsl(${selDomain.hue} 85% 65%)` }}>Domain</div>
                <h2 className="mt-2 font-serif text-2xl text-gold-luminous">{selDomain.name}</h2>
                {selDomain.description && <p className="mt-2 text-sm text-foreground/80">{selDomain.description}</p>}
                <Link to="/academy/kafrequency/$domain" params={{ domain: selDomain.slug }}
                  className="mt-5 inline-flex rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian">
                  Travel into {selDomain.name}
                </Link>
              </>
            ) : (
              <p className="text-sm text-foreground/70">Select a node to travel into that domain.</p>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}
